(() => {
  if (typeof window === 'undefined') return;
  if (!new URLSearchParams(location.search).has('fx')) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const VERT = `
    attribute vec2 a_pos;
    varying vec2 v_uv;
    void main() {
      v_uv = a_pos * 0.5 + 0.5;
      gl_Position = vec4(a_pos, 0.0, 1.0);
    }
  `;

  // Adapted from Unicorn Studio's "God Rays (custom)" effect: combines GPU Gems 3
  // style accumulation with interleaved-gradient-noise dither, perpendicular wiggle,
  // and per-sample luminance threshold so only bright samples contribute.
  const FRAG = `
    precision highp float;
    uniform sampler2D u_tex;
    uniform vec2 u_res;
    uniform vec2 u_imgRes;
    uniform vec2 u_mouse;
    uniform vec2 u_window;   // light pos in image uv
    uniform vec2 u_parallax;
    uniform float u_fadeIn;  // section-visible fade (0..1)

    uniform float u_strength;    // rays overall strength
    uniform float u_amount;      // 0..1 controls both step length and decay
    uniform float u_exposure;    // 0..1 — higher = brighter rays
    uniform float u_diffusion;   // perpendicular wiggle amplitude
    uniform float u_time;
    uniform float u_expNoiseMin; // noise low bound added to exposure
    uniform float u_expNoiseMax; // noise high bound added to exposure
    uniform float u_noiseSpeed;  // rate of exposure noise over time
    uniform vec3  u_tint;

    varying vec2 v_uv;

    const float MAX_ITERATIONS = 32.0;

    vec2 coverUv(vec2 uv) {
      float cAsp = u_res.x / u_res.y;
      float iAsp = u_imgRes.x / u_imgRes.y;
      if (cAsp > iAsp) {
        float s = iAsp / cAsp;
        return vec2(uv.x, 0.5 + (uv.y - 0.5) * s);
      }
      float s = cAsp / iAsp;
      return vec2(0.5 + (uv.x - 0.5) * s, uv.y);
    }

    vec2 imgToScreen(vec2 imgUv) {
      float cAsp = u_res.x / u_res.y;
      float iAsp = u_imgRes.x / u_imgRes.y;
      if (cAsp > iAsp) {
        float s = iAsp / cAsp;
        return vec2(imgUv.x, 0.5 + (imgUv.y - 0.5) / s);
      }
      float s = cAsp / iAsp;
      return vec2(0.5 + (imgUv.x - 0.5) / s, imgUv.y);
    }

    float ign(vec2 st) {
      return fract(52.9829189 * fract(dot(st, vec2(0.06711056, 0.00583715))));
    }

    // Smooth 1D value noise in [0, 1] — used to animate exposure over time.
    float vnoise1(float t) {
      float i = floor(t);
      float f = fract(t);
      f = f * f * (3.0 - 2.0 * f);
      float a = fract(sin(i * 127.1) * 43758.5453);
      float b = fract(sin((i + 1.0) * 127.1) * 43758.5453);
      return mix(a, b, f);
    }

    vec3 sampleTex(vec2 screenUv) {
      return texture2D(u_tex, coverUv(clamp(screenUv, 0.0, 1.0))).rgb;
    }

    vec3 godRays(vec2 st, vec2 lightScreen) {
      vec3 color = vec3(0.0);
      float decay = mix(0.89, 0.965, u_amount);
      float weight = 1.0;
      vec2 stepDir = (lightScreen - st) / MAX_ITERATIONS * (0.25 + min(1.0, u_amount)) * 0.75;
      float noise = ign(st * u_res);
      vec2 sampleUv = st + stepDir * noise;
      vec2 perpDir = vec2(-stepDir.y, stepDir.x);
      float intensity = 2.8 * u_strength;

      // Time-based value noise modulates exposure within user-defined range.
      float n = vnoise1(u_time * u_noiseSpeed);
      float expDelta = mix(u_expNoiseMin, u_expNoiseMax, n);
      float animExp = clamp(u_exposure + expDelta, 0.0, 1.0);
      // Invert the Unicorn-style "threshold" into a "brightness" slider:
      // higher u_exposure -> lower threshold -> brighter rays.
      float T = 1.0 - animExp;

      for (int i = 0; i < 32; i++) {
        float theta = float(i) / MAX_ITERATIONS;
        sampleUv += stepDir + (perpDir * theta * sin(noise * 0.25 * (1.0 + theta) * 50.0)) * u_diffusion;
        vec3 s = sampleTex(sampleUv);
        float lum = dot(s, vec3(0.299, 0.587, 0.114));
        float thresh = smoothstep(T - 0.1, T, lum);
        color += s * thresh * weight * intensity;
        weight *= decay;
        if (weight < 0.05) break;
      }
      return color / MAX_ITERATIONS;
    }

    void main() {
      vec3 base = sampleTex(v_uv);

      vec2 lightImg = u_window + (u_mouse - 0.5) * u_parallax;
      vec2 lightScreen = imgToScreen(lightImg);

      vec3 rays = godRays(v_uv, lightScreen) * u_tint;

      // Screen blend — caps at 1.0, so bright areas (the window itself) don't overexpose.
      vec3 add = clamp(rays * u_fadeIn, 0.0, 1.0);
      vec3 outCol = 1.0 - (1.0 - base) * (1.0 - add);
      gl_FragColor = vec4(outCol, 1.0);
    }
  `;

  const DEFAULTS = {
    strength: 1.79,
    amount: 0.85,
    exposure: 0.15,
    diffusion: 0.16,
    expNoiseMin: -0.195,
    expNoiseMax: -0.1,
    noiseSpeed: 0.6,
    tintR: 1.08,
    tintG: 0.98,
    tintB: 0.78,
    parallaxX: 0.12,
    parallaxY: 0.08,
  };

  const params = Object.assign({}, DEFAULTS);
  window.__homeVisualTweak = { params, defaults: DEFAULTS };

  // Preload GSAP in parallel with scene init so the intro tween can fire as
  // soon as the texture is ready.
  const gsapReady = (async () => {
    try {
      const mod = await import('https://cdn.jsdelivr.net/npm/gsap@3.12.5/+esm');
      return mod.gsap || mod.default?.gsap || mod.default || null;
    } catch (err) {
      console.warn('[home-visual] GSAP load failed', err);
      return null;
    }
  })();

  const start = () => {
    document.querySelectorAll('.home-visual').forEach(initSection);
    maybeLoadTweakpane();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  function initSection(section) {
    if (section.dataset.webglInit === '1') return;
    const media = section.querySelector('.home-visual__media');
    if (!media) return;
    const img = media.querySelector('img');
    if (!img) return;

    const gl = (() => {
      const c = document.createElement('canvas');
      return c.getContext('webgl', { antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
    })();
    if (!gl) return;

    section.dataset.webglInit = '1';

    const canvas = gl.canvas;
    canvas.className = 'home-visual__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    media.appendChild(canvas);

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.warn('[home-visual] shader:', gl.getShaderInfoLog(sh));
        gl.deleteShader(sh);
        return null;
      }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { canvas.remove(); return; }

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[home-visual] link:', gl.getProgramInfoLog(prog));
      canvas.remove();
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const u = {
      tex: gl.getUniformLocation(prog, 'u_tex'),
      res: gl.getUniformLocation(prog, 'u_res'),
      imgRes: gl.getUniformLocation(prog, 'u_imgRes'),
      mouse: gl.getUniformLocation(prog, 'u_mouse'),
      window: gl.getUniformLocation(prog, 'u_window'),
      fadeIn: gl.getUniformLocation(prog, 'u_fadeIn'),
      strength: gl.getUniformLocation(prog, 'u_strength'),
      amount: gl.getUniformLocation(prog, 'u_amount'),
      exposure: gl.getUniformLocation(prog, 'u_exposure'),
      diffusion: gl.getUniformLocation(prog, 'u_diffusion'),
      time: gl.getUniformLocation(prog, 'u_time'),
      expNoiseMin: gl.getUniformLocation(prog, 'u_expNoiseMin'),
      expNoiseMax: gl.getUniformLocation(prog, 'u_expNoiseMax'),
      noiseSpeed: gl.getUniformLocation(prog, 'u_noiseSpeed'),
      tint: gl.getUniformLocation(prog, 'u_tint'),
      parallax: gl.getUniformLocation(prog, 'u_parallax'),
    };
    const nullUniforms = Object.entries(u).filter(([, v]) => v === null).map(([k]) => k);
    if (nullUniforms.length) {
      console.warn('[home-visual] missing uniform locations:', nullUniforms);
    } else {
      console.info('[home-visual] all uniforms bound:', Object.keys(u));
    }

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const state = {
      ready: false,
      imgW: 1,
      imgH: 1,
      mouseTx: 0.5,
      mouseTy: 0.5,
      mouseX: 0.5,
      mouseY: 0.5,
      intensity: 0,
      progress: 0,
      visible: true,
      window: readWindowCenter(section),
    };
    window.__homeVisualTweak.state = state;

    const loader = new Image();
    loader.crossOrigin = 'anonymous';
    loader.decoding = 'async';
    loader.onload = async () => {
      state.imgW = loader.naturalWidth || 1;
      state.imgH = loader.naturalHeight || 1;
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, loader);
      state.ready = true;
      requestAnimationFrame(() => canvas.classList.add('is-ready'));

      const gsap = await gsapReady;
      if (gsap) {
        gsap.to(state, { progress: 1, duration: 3, ease: 'power2.out' });
      }
    };
    loader.onerror = () => canvas.remove();

    const pickSrc = () => img.currentSrc || img.src;
    if (img.complete && pickSrc()) {
      loader.src = pickSrc();
    } else {
      img.addEventListener('load', () => { loader.src = pickSrc(); }, { once: true });
    }

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      const w = media.clientWidth || window.innerWidth;
      const h = media.clientHeight || window.innerHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    window.addEventListener('pointermove', (e) => {
      state.mouseTx = e.clientX / window.innerWidth;
      state.mouseTy = 1 - e.clientY / window.innerHeight;
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        state.visible = entries[0].isIntersecting;
      }, { threshold: 0 });
      io.observe(media);
    }

    let prev = performance.now();
    const startTime = prev;

    const frame = (now) => {
      requestAnimationFrame(frame);
      if (!state.ready) return;

      const dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;

      const ease = 1 - Math.pow(0.001, dt);
      state.mouseX += (state.mouseTx - state.mouseX) * ease;
      state.mouseY += (state.mouseTy - state.mouseY) * ease;

      const target = state.visible ? 1 : 0;
      state.intensity += (target - state.intensity) * Math.min(1, dt * 3);

      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(prog);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(u.tex, 0);
      gl.uniform2f(u.res, canvas.width, canvas.height);
      gl.uniform2f(u.imgRes, state.imgW, state.imgH);
      gl.uniform2f(u.mouse, state.mouseX, state.mouseY);
      gl.uniform2f(u.window, state.window.x, state.window.y);
      gl.uniform1f(u.fadeIn, state.intensity);
      gl.uniform1f(u.strength, params.strength * state.progress);
      gl.uniform1f(u.amount, params.amount * state.progress);
      gl.uniform1f(u.exposure, params.exposure);
      gl.uniform1f(u.diffusion, params.diffusion);
      gl.uniform1f(u.time, (now - startTime) / 1000);
      gl.uniform1f(u.expNoiseMin, params.expNoiseMin);
      gl.uniform1f(u.expNoiseMax, params.expNoiseMax);
      gl.uniform1f(u.noiseSpeed, params.noiseSpeed);
      gl.uniform3f(u.tint, params.tintR, params.tintG, params.tintB);
      gl.uniform2f(u.parallax, params.parallaxX, params.parallaxY);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    requestAnimationFrame(frame);

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      canvas.remove();
    });
  }

  function readWindowCenter(section) {
    const ds = section.dataset;
    const x = parseFloat(ds.windowX);
    const y = parseFloat(ds.windowY);
    return {
      x: Number.isFinite(x) ? x : 0.5,
      y: Number.isFinite(y) ? y : 0.5,
    };
  }

  async function maybeLoadTweakpane() {
    if (!new URLSearchParams(location.search).has('tweak')) return;

    console.info('[home-visual] loading Tweakpane');

    const sources = [
      'https://cdn.jsdelivr.net/npm/tweakpane@4.0.5/+esm',
      'https://esm.sh/tweakpane@4.0.5',
      'https://unpkg.com/tweakpane@4.0.5/dist/tweakpane.min.js?module',
    ];
    for (const src of sources) {
      try {
        const mod = await import(/* @vite-ignore */ src);
        const Pane = mod.Pane || (mod.default && mod.default.Pane);
        if (!Pane) {
          console.warn('[home-visual] no Pane export from', src, mod);
          continue;
        }
        console.info('[home-visual] Tweakpane loaded from', src);
        setupPane(Pane);
        return;
      } catch (err) {
        console.warn('[home-visual] failed to load', src, err);
      }
    }
    console.error('[home-visual] all Tweakpane sources failed — possibly blocked by CSP');
  }

  function setupPane(Pane) {
    const pane = new Pane({ title: 'Home visual — light', expanded: true });

    const state = window.__homeVisualTweak.state;

    const rays = pane.addFolder({ title: 'God rays' });
    rays.addBinding(params, 'strength', { min: 0, max: 3, step: 0.01 });
    rays.addBinding(params, 'amount', { min: 0, max: 1, step: 0.01 });
    rays.addBinding(params, 'exposure', { min: 0, max: 1, step: 0.005 });
    rays.addBinding(params, 'diffusion', { min: 0, max: 3, step: 0.01 });

    const flow = pane.addFolder({ title: 'Exposure noise' });
    flow.addBinding(params, 'expNoiseMin', { min: -0.5, max: 0.5, step: 0.005 });
    flow.addBinding(params, 'expNoiseMax', { min: -0.5, max: 0.5, step: 0.005 });
    flow.addBinding(params, 'noiseSpeed', { min: 0, max: 3, step: 0.01 });

    const tint = pane.addFolder({ title: 'Warm tint' });
    tint.addBinding(params, 'tintR', { min: 0, max: 2, step: 0.01 });
    tint.addBinding(params, 'tintG', { min: 0, max: 2, step: 0.01 });
    tint.addBinding(params, 'tintB', { min: 0, max: 2, step: 0.01 });

    const mouse = pane.addFolder({ title: 'Mouse parallax' });
    mouse.addBinding(params, 'parallaxX', { min: 0, max: 0.5, step: 0.001 });
    mouse.addBinding(params, 'parallaxY', { min: 0, max: 0.5, step: 0.001 });

    if (state) {
      const win = pane.addFolder({ title: 'Window center' });
      win.addBinding(state.window, 'x', { min: 0, max: 1, step: 0.01, label: 'window.x' });
      win.addBinding(state.window, 'y', { min: 0, max: 1, step: 0.01, label: 'window.y' });
    }

    pane.addButton({ title: 'Reset defaults' }).on('click', () => {
      Object.assign(params, DEFAULTS);
      pane.refresh();
    });
    pane.addButton({ title: 'Log params' }).on('click', () => {
      console.log('home-visual params:', JSON.parse(JSON.stringify(params)));
    });
  }
})();
